import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AppButton } from '../ui';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    // Log error to monitoring service in production
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return (
        <div className="error-boundary">
          <div className="error-boundary__content">
            <div className="error-boundary__icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="24" cy="24" r="20" />
                <line x1="24" y1="14" x2="24" y2="26" strokeLinecap="round" />
                <circle cx="24" cy="32" r="1.5" fill="currentColor" stroke="none" />
              </svg>
            </div>
            <h2 className="error-boundary__title">Something went wrong</h2>
            <p className="error-boundary__message">
              {error?.message || 'An unexpected error occurred. Please try again.'}
            </p>
            <div className="error-boundary__actions">
              <AppButton variant="primary" onClick={this.handleRetry}>
                Try Again
              </AppButton>
              <AppButton variant="ghost" onClick={this.handleReload}>
                Reload Page
              </AppButton>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

interface InlineErrorProps {
  message: string;
  onRetry?: () => void;
}

export const InlineError: React.FC<InlineErrorProps> = ({ message, onRetry }) => (
  <div className="inline-error" role="alert">
    <div className="inline-error__content">
      <svg className="inline-error__icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 10.5a.75.75 0 110-1.5.75.75 0 010 1.5zM8.75 8a.75.75 0 01-1.5 0V5a.75.75 0 011.5 0v3z" />
      </svg>
      <span className="inline-error__message">{message}</span>
    </div>
    {onRetry && (
      <button className="inline-error__retry" onClick={onRetry} type="button">
        Retry
      </button>
    )}
  </div>
);

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
}) => (
  <div className="empty-state">
    {icon && <div className="empty-state__icon">{icon}</div>}
    <h3 className="empty-state__title">{title}</h3>
    {description && <p className="empty-state__description">{description}</p>}
    {action && (
      <AppButton variant="primary" onClick={action.onClick}>
        {action.label}
      </AppButton>
    )}
  </div>
);
